// Magnus Assistant for PFMEA Portal
// ============================================================
// Context-aware AI assistant with Basic and Prime modes
// Basic: Navigation, quick answers, PFMEA knowledge (no LLM)
// Prime: Deep analysis with Gemini LLM
// ============================================================

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Send, Sparkles, Trash2, ChevronRight, Zap, Brain } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { PFMEA_AGENTS, getRecommendedAgent } from '../constants/aiAgents';
import { handleBasicQuery, PageContext } from './MagnusQueryHandlers';

interface Message {
    role: 'user' | 'magnus';
    content: string;
    action?: {
        type: 'navigate';
        target: string;
        label?: string;
    };
    agentId?: string;
}

// ============================================================
// PFMEA PAGE CONTEXT - Navigation awareness
// ============================================================
const PAGE_CONTEXT: Record<string, PageContext> = {
    '/': {
        name: 'PFMEA Dashboard',
        purpose: 'Overview of process risk metrics, 4M distribution, workflow progress, and quick actions',
        nextSteps: ['Review high-priority items', 'Check overdue actions', 'Create process flow', 'Use AI ideation'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Table', why: 'View all failure modes' },
            { path: '/process-flow', label: 'Process Flow', why: 'Define manufacturing steps' },
            { path: '/ideation', label: 'AI Ideation', why: 'Generate PFMEA with AI' },
        ],
        aiagVdaStep: [1],
    },
    '/process-flow': {
        name: 'Process Flow Diagram',
        purpose: 'Define and visualize manufacturing process steps with symbols and flow',
        nextSteps: ['Add process steps', 'Mark special characteristics', 'Link to PFMEA items', 'Generate with AI'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Table', why: 'Analyze failures for each step' },
            { path: '/ideation', label: 'AI Ideation', why: 'Auto-generate process steps' },
        ],
        aiagVdaStep: [2, 3],
    },
    '/pfmea': {
        name: 'PFMEA Worksheet',
        purpose: 'Analyze process failure modes with 4M categories, effects, causes, and S/O/D ratings',
        nextSteps: ['Add failure modes', 'Rate severity/occurrence/detection', 'Define controls', 'Create recommended actions'],
        relatedPages: [
            { path: '/control-plan', label: 'Control Plan', why: 'Generate controls from PFMEA' },
            { path: '/ideation', label: 'AI Ideation', why: 'Generate more failure modes' },
            { path: '/process-flow', label: 'Process Flow', why: 'Link to process steps' },
        ],
        aiagVdaStep: [4, 5],
    },
    '/ideation': {
        name: 'AI PFMEA Ideation',
        purpose: 'Generate PFMEA content using AI - process steps, failure modes, 4M analysis',
        nextSteps: ['Describe your process', 'Generate steps', 'Generate failures', 'Review and save'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Table', why: 'View generated items' },
            { path: '/process-flow', label: 'Process Flow', why: 'View generated steps' },
        ],
        aiagVdaStep: [2, 3, 4],
    },
    '/control-plan': {
        name: 'Control Plan',
        purpose: 'Document process controls, inspection methods, sample sizes, and reaction plans',
        nextSteps: ['Generate from PFMEA', 'Define sample sizes', 'Add reaction plans', 'Export AIAG format'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Table', why: 'Link to PFMEA items' },
            { path: '/work-instructions', label: 'Work Instructions', why: 'Create operator instructions' },
        ],
        aiagVdaStep: [6, 7],
    },
    '/work-instructions': {
        name: 'Work Instructions',
        purpose: 'Create detailed operator instructions with images, videos, and revision control',
        nextSteps: ['Create instructions', 'Add visual aids', 'Link to process steps', 'Manage revisions'],
        relatedPages: [
            { path: '/control-plan', label: 'Control Plan', why: 'Link to controls' },
            { path: '/process-flow', label: 'Process Flow', why: 'Link to steps' },
        ],
        aiagVdaStep: [7],
    },
    '/settings': {
        name: 'Settings',
        purpose: 'Configure PFMEA standard, project settings, theme, and user preferences',
        nextSteps: ['Select standard', 'Choose project', 'Set theme', 'View permissions'],
        relatedPages: [
            { path: '/', label: 'Dashboard', why: 'Return to overview' },
        ],
        aiagVdaStep: [1],
    },
    '/function-matrix': {
        name: 'Function Matrix',
        purpose: 'Map process steps to product requirements in a visual checkbox grid (AIAG-VDA Step 3)',
        nextSteps: ['Link steps to requirements', 'Analyze function relationships', 'Identify gaps'],
        relatedPages: [
            { path: '/process-flow', label: 'Process Flow', why: 'Define steps first' },
            { path: '/pfmea', label: 'PFMEA Table', why: 'Analyze functions for failures' },
        ],
        aiagVdaStep: [3],
    },
    '/project-scoping': {
        name: 'Project Scoping (5T Dashboard)',
        purpose: '5T Framework: InTent, Timing, Team, Tools, Tasks - Complete AIAG-VDA Step 1 planning',
        nextSteps: ['Define project intent', 'Set milestones', 'Assign team members', 'Track tasks'],
        relatedPages: [
            { path: '/', label: 'Dashboard', why: 'View overall progress' },
            { path: '/workflow', label: 'Workflow', why: 'Track approvals' },
        ],
        aiagVdaStep: [1],
    },
    '/failure-net': {
        name: 'Failure Net Visualization',
        purpose: 'Interactive D3.js graph showing 5-level AIAG-VDA failure chain: Cause → Mode → Local → Next → End Effect',
        nextSteps: ['Click nodes to trace paths', 'Review 4M categorization', 'Analyze control placement'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Editor', why: 'Edit failure data' },
            { path: '/process-flow', label: 'Process Flow', why: 'See process context' },
        ],
        aiagVdaStep: [5],
    },
    '/workflow': {
        name: 'Workflow Dashboard',
        purpose: 'Track PFMEA approval workflow with status, signatures, and revision history',
        nextSteps: ['Review pending approvals', 'Submit items for review', 'Track changes'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Editor', why: 'Edit items' },
            { path: '/', label: 'Dashboard', why: 'View metrics' },
        ],
        aiagVdaStep: [7],
    },
    '/actions': {
        name: 'Action Tracker',
        purpose: 'Manage recommended actions with owners, due dates, and risk reduction tracking',
        nextSteps: ['Assign actions', 'Set due dates', 'Track completion', 'Review revised ratings'],
        relatedPages: [
            { path: '/pfmea', label: 'PFMEA Editor', why: 'View linked PFMEA items' },
            { path: '/', label: 'Dashboard', why: 'View action metrics' },
        ],
        aiagVdaStep: [6],
    },
};

// Note: PFMEA_KNOWLEDGE and handleBasicQuery are now imported from MagnusQueryHandlers.tsx

// ============================================================
// MAIN COMPONENT
// ============================================================
export const MagnusAssistant: React.FC = () => {
    const location = useLocation();
    const reactNavigate = useNavigate();
    useAuth(); // For potential future use with hasPermission
    const { pfmeaItems, metrics, activeProject, standardConfig } = useAppContext();
    const currentPageContext = PAGE_CONTEXT[location.pathname];

    const [isOpen, setIsOpen] = useState(false);
    const [showIntroBubble, setShowIntroBubble] = useState(true);
    const [isPrimeMode, setIsPrimeMode] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'magnus',
            content: currentPageContext
                ? `Hey! You're on **${currentPageContext.name}**. ${currentPageContext.purpose}\n\nI can help you **navigate** (Basic Mode) or **analyze deeply** (Prime Mode).`
                : "Hello! I'm Magnus. I can help you navigate the PFMEA app or analyze your data!"
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Update greeting when page changes
    useEffect(() => {
        const ctx = PAGE_CONTEXT[location.pathname];
        const greeting = ctx
            ? `You're now on **${ctx.name}**. ${ctx.purpose}\n\nThis is Step ${ctx.aiagVdaStep.join('/')} of the AIAG-VDA workflow.`
            : "How can I help you?";

        setMessages([{ role: 'magnus', content: greeting }]);
    }, [location.pathname]);

    const handleClear = () => {
        const ctx = PAGE_CONTEXT[location.pathname];
        setMessages([{
            role: 'magnus',
            content: ctx ? `Hey! You're on **${ctx.name}**. What would you like to do?` : "Hello! How can I help you?"
        }]);
    };

    const handleSend = async () => {
        if (!inputValue.trim()) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

        if (isPrimeMode) {
            // Prime Mode - Would call LLM (simulated for now)
            setIsThinking(true);

            // Build context for LLM
            const contextInfo = {
                page: currentPageContext?.name,
                project: activeProject,
                standard: standardConfig?.name,
                totalItems: metrics.totalPfmeaItems,
                highPriority: metrics.byActionPriority.high,
                openActions: metrics.openActions,
                by4M: metrics.by4M,
                agent: selectedAgent ? PFMEA_AGENTS.find(a => a.id === selectedAgent) : null,
            };

            // Simulate LLM call (replace with real geminiService call)
            setTimeout(() => {
                const agentInfo = contextInfo.agent ? `\n\n🤖 *Using ${contextInfo.agent.name}*` : '';
                setMessages(prev => [...prev, {
                    role: 'magnus',
                    content: `**[Prime Mode]** Analyzing your question with full context...${agentInfo}\n\n` +
                        `Based on your ${contextInfo.totalItems} PFMEA items for "${contextInfo.project}":\n\n` +
                        `Your question was: "${userMessage}"\n\n` +
                        `*In full implementation, this would call the Gemini LLM with your project context, current page (${contextInfo.page}), and selected standard (${contextInfo.standard}).*\n\n` +
                        `Would you like me to help with something specific?`,
                    agentId: selectedAgent || undefined,
                }]);
                setIsThinking(false);
            }, 1500);
        } else {
            // Basic Mode - No LLM
            const response = handleBasicQuery(userMessage, currentPageContext, metrics, pfmeaItems);
            setMessages(prev => [...prev, {
                role: 'magnus',
                content: response.answer,
                action: response.action,
            }]);
        }
    };

    const handleAction = (action: Message['action']) => {
        if (action?.type === 'navigate') {
            reactNavigate(action.target);
            setIsOpen(false);
        }
    };

    // Recommended agent for current page
    const recommendedAgent = useMemo(() => {
        const ctx = PAGE_CONTEXT[location.pathname];
        if (!ctx) return null;
        return getRecommendedAgent(ctx.aiagVdaStep[0] || 1);
    }, [location.pathname]);

    return (
        <>
            {/* Intro Bubble */}
            {!isOpen && showIntroBubble && (
                <div className="fixed bottom-24 right-6 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-xl border-l-4 border-purple-500 max-w-xs z-40 animate-fade-in">
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-purple-600 dark:text-purple-400">Hi, I&apos;m Magnus! 👋</h4>
                        <button onClick={(e) => { e.stopPropagation(); setShowIntroBubble(false); }} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        I have two modes:
                        <br /><span className="font-semibold text-blue-500">⚡ Basic:</span> I&apos;ll help you navigate.
                        <br /><span className="font-semibold text-yellow-500">🚀 Prime:</span> I&apos;ll analyze your data deeply.
                    </p>
                    <div className="absolute bottom-[-6px] right-8 w-4 h-4 bg-white dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 transform rotate-45"></div>
                </div>
            )}

            {/* Floating Magnus Button with icon */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center group"
                title="Magnus"
                data-tour-id="magnus-assistant"
            >
                <img
                    src="/magnus-icon.png"
                    alt="Magnus"
                    className="w-10 h-10 group-hover:scale-110 transition-transform"
                    onError={(e) => {
                        // Fallback to Brain icon if image fails to load
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                />
                <Brain className="hidden w-8 h-8" />
                {!isOpen && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></span>
                )}
            </button>

            {/* Chat Panel */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 z-50 w-96 h-[600px] bg-[var(--bg-secondary)] rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-purple-200 dark:border-purple-700">
                    {/* Header - Matching original Magnus */}
                    <div className={`p-4 flex items-center justify-between transition-colors duration-500 ${isPrimeMode
                        ? 'bg-gradient-to-r from-slate-900 via-purple-900 to-slate-800'
                        : 'bg-gradient-to-r from-purple-500 to-blue-600'}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                {isPrimeMode ? <Zap className="text-yellow-400" size={20} /> : <Sparkles className="text-white" size={20} />}
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-lg">Magnus</h3>
                                <p className="text-purple-100 text-xs">Reliability Assistant</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsPrimeMode(!isPrimeMode)}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${isPrimeMode
                                    ? 'bg-yellow-400 text-purple-900 border-yellow-300 shadow-[0_0_10px_rgba(250,204,21,0.5)]'
                                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                                    }`}
                            >
                                {isPrimeMode ? '🚀 PRIME' : '⚡ BASIC'}
                            </button>
                            <button
                                onClick={handleClear}
                                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                                title="Clear Chat"
                            >
                                <Trash2 size={16} />
                            </button>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Agent Selector (Prime Mode) */}
                    {isPrimeMode && (
                        <div className="p-3 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
                            <p className="text-xs text-[var(--text-secondary)] mb-2">Select AI Agent:</p>
                            <div className="flex flex-wrap gap-2">
                                {PFMEA_AGENTS.slice(0, 4).map(agent => (
                                    <button
                                        key={agent.id}
                                        onClick={() => setSelectedAgent(selectedAgent === agent.id ? null : agent.id)}
                                        className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 transition-all ${selectedAgent === agent.id
                                            ? 'bg-purple-500 text-white'
                                            : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-purple-100 dark:hover:bg-purple-900'
                                            }`}
                                    >
                                        <span>{agent.icon}</span>
                                        {agent.name.split(' ')[0]}
                                    </button>
                                ))}
                            </div>
                            {recommendedAgent && selectedAgent !== recommendedAgent.id && (
                                <p className="text-xs text-purple-500 mt-2">
                                    💡 Recommended: {recommendedAgent.name}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl p-3 ${msg.role === 'user'
                                    ? 'bg-primary-500 text-white rounded-br-none'
                                    : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] rounded-bl-none'
                                    }`}>
                                    {msg.agentId && (
                                        <p className="text-xs opacity-70 mb-1">
                                            🤖 {PFMEA_AGENTS.find(a => a.id === msg.agentId)?.name}
                                        </p>
                                    )}
                                    <div className="text-sm whitespace-pre-wrap">
                                        {msg.content.split(/(\*\*.*?\*\*)/g).map((part, i) => {
                                            if (part.startsWith('**') && part.endsWith('**')) {
                                                return <strong key={i} className="text-primary-600 dark:text-primary-400">{part.slice(2, -2)}</strong>;
                                            }
                                            return <span key={i}>{part}</span>;
                                        })}
                                    </div>
                                    {msg.action && (
                                        <button
                                            onClick={() => handleAction(msg.action)}
                                            className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-white rounded-lg text-xs font-medium hover:bg-primary-600 transition-colors"
                                        >
                                            {msg.action.label}
                                            <ChevronRight size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isThinking && (
                            <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                                <div className="animate-pulse">🤖</div>
                                <div className="text-sm">Magnus is thinking...</div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-[var(--border-color)]">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder={isPrimeMode ? "Ask anything about your PFMEA..." : "Ask a question or request a page..."}
                                className="flex-1 px-4 py-2 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                disabled={isThinking}
                            />
                            <button
                                onClick={handleSend}
                                disabled={!inputValue.trim() || isThinking}
                                className="p-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send size={20} />
                            </button>
                        </div>
                        <p className="text-xs text-[var(--text-secondary)] mt-2 text-center">
                            {isPrimeMode ? '🔮 Prime: Deep AI analysis' : '⚡ Basic: Quick navigation & answers'}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default MagnusAssistant;
